import {
  AfterViewInit, ChangeDetectorRef, Component, ElementRef, forwardRef, Input, OnChanges, OnDestroy, SimpleChanges, ViewChild
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import {
  formatHtml, lintMarkup, MarkupEditorOptions, MarkupFeature, MarkupToken, NO_MARKUP_OPTIONS, resolveFeatures
} from './markup-editor-rules';
import type { MarkupEditorConfig, MarkupEditorHandle } from './markup-editor.codemirror';

/**
 * A code editor for markup with {{Placeholder}} tokens (CodeMirror 6), usable with [(ngModel)] like a textarea.
 *
 * Each option is an input flag (the page maps them from the emailtemplates.rich-editor.* sub-switches), and the
 * extensions they turn on are `features` — the page and the tests read that set. CodeMirror itself is loaded
 * with a dynamic import the first time the component is created, so it stays out of the main bundle; `ready`
 * resolves once the editor is on screen.
 *
 * Every user edit — typing, a placeholder inserted with insertAtCursor, Format — is reported through the
 * form control, so (ngModelChange) sees them all. Values written by the form are not reported back.
 */
@Component({
  selector: 'app-markup-editor',
  standalone: true,
  templateUrl: 'markup-editor.component.html',
  providers: [{ provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => MarkupEditorComponent), multi: true }]
})
export class MarkupEditorComponent implements ControlValueAccessor, AfterViewInit, OnChanges, OnDestroy {

  /** This email's placeholders: what the chips, suggestions and lint check against. */
  @Input() tokens: MarkupToken[] = [];
  /** HTML wording (highlighting, tag closing, Format) or plain text. */
  @Input() html: boolean = true;
  @Input() options: MarkupEditorOptions = NO_MARKUP_OPTIONS;
  /** The rest of the wording (the subject): a required placeholder there counts as kept. */
  @Input() alsoUses: string = '';
  /** The id of the element labelling the editor. */
  @Input() labelledBy: string | null = null;

  @ViewChild('host', { static: true }) host: ElementRef<HTMLDivElement>;

  public value: string = '';
  public loading: boolean = true;
  public loadFailed: boolean = false;
  public features: MarkupFeature[] = [];
  public missingRequired: MarkupToken[] = [];

  /** Resolves once CodeMirror is loaded and the editor is on screen (or loading failed). */
  public readonly ready: Promise<void>;
  private resolveReady: () => void;

  private handle: MarkupEditorHandle | null = null;
  private destroyed = false;
  private onChange: (value: string) => void = () => { };
  private onTouched: () => void = () => { };

  constructor(private cdr: ChangeDetectorRef, private element: ElementRef<HTMLElement>) {
    this.ready = new Promise<void>(resolve => this.resolveReady = resolve);
  }

  /** A token as written in the wording: {{Name}}. */
  public written(token: MarkupToken): string {
    return '{{' + token.name + '}}';
  }

  public has(feature: MarkupFeature): boolean {
    return this.features.includes(feature);
  }

  ngOnChanges(changes: SimpleChanges): void {
    this.features = resolveFeatures(this.options, this.html);
    this.updateMissing();
    if (!this.handle) {
      return;
    }
    if (changes['options'] || changes['html'] || changes['tokens'] || changes['labelledBy']) {
      this.handle.reconfigure(this.config());
    } else if (changes['alsoUses']) {
      this.handle.refresh();
    }
  }

  async ngAfterViewInit(): Promise<void> {
    try {
      const cm = await import('./markup-editor.codemirror');
      if (this.destroyed) {
        return;
      }
      this.handle = cm.createMarkupEditor(this.host.nativeElement, this.value, this.config(), value => {
        this.value = value;
        this.updateMissing();
        this.onChange(value);
        this.cdr.markForCheck();
      });
      this.handle.view.contentDOM.addEventListener('blur', () => this.onTouched());
    } catch (e) {
      console.error('The markup editor could not be loaded.', e);
      this.loadFailed = true;
    } finally {
      this.loading = false;
      this.cdr.markForCheck();
      this.resolveReady();
    }
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    this.handle?.destroy();
    this.handle = null;
  }

  // ── ControlValueAccessor ───────────────────────────────────────────────────────────────────────

  writeValue(value: string | null): void {
    this.value = value ?? '';
    this.updateMissing();
    this.handle?.setValue(this.value);
    this.cdr.markForCheck();
  }

  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  // ── Commands ───────────────────────────────────────────────────────────────────────────────────

  /** Types `text` at the cursor, over any selection. Before the editor has loaded, adds it at the end. */
  public insertAtCursor(text: string): void {
    if (this.handle) {
      this.handle.insertAtCursor(text);
      return;
    }
    this.value = this.value + text;
    this.updateMissing();
    this.onChange(this.value);
    this.cdr.markForCheck();
  }

  /** Lays the HTML out one block per line; text and placeholders are untouched. Undo reverses it. */
  public format(): void {
    if (!this.has('format')) {
      return;
    }
    const next = formatHtml(this.value);
    if (next === this.value) {
      return;
    }
    if (this.handle) {
      this.handle.replaceAll(next);
    } else {
      this.value = next;
      this.onChange(next);
    }
    this.handle?.focus();
  }

  public focus(): void {
    this.handle?.focus();
  }

  /** The CodeMirror view, once loaded (tests and diagnostics only). */
  public get view() {
    return this.handle?.view ?? null;
  }

  public get hostElement(): HTMLElement {
    return this.element.nativeElement;
  }

  private config(): MarkupEditorConfig {
    return {
      features: this.features,
      tokens: this.tokens || [],
      alsoUses: () => this.alsoUses || '',
      ariaLabelledBy: this.labelledBy
    };
  }

  private updateMissing(): void {
    this.missingRequired = this.has('lint') ? lintMarkup(this.value, this.tokens, this.alsoUses).missingRequired : [];
  }
}
